import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {IonContent, Platform} from '@ionic/angular';

import {CustomerModel} from 'src/app/models/customer-model';
import {AuthenticationService} from 'src/app/services/authentication/authentication.service';
import {SuccessMessageService} from 'src/app/services/message-provider/success-message.service';
import {UniqueIDService} from 'src/app/services/unique-id/unique-id.service';
import {
  eMfaStatuses,
  UserSessionService,
} from 'src/app/services/user-session/user-session.service';
import {Navigator} from 'src/app/components/menu/navigator';
import {LogoutService} from 'src/app/services/logout/logout.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { Subscription } from 'rxjs';
import { EventsService } from 'src/app/services/event/event.service';

@Component({
  selector: 'app-sms-login',
  templateUrl: './sms-login.component.html',
  styleUrls: ['./sms-login.component.scss'],
})
export class SmsLoginComponent implements OnInit {
  @ViewChild(IonContent, {static: false}) content: IonContent;

  @Input() customerId: any;
  @Input() callback: () => void;
  @Input() governmentId: any;
  @Input() onClose: () => void;
  @Input() isMFAFirst: any;
  @Input() isRegistration: boolean;

  error = null;
  verifiedCode: boolean;
  showMessageSMSSent: boolean;
  customer: CustomerModel;
  channels: string[];
  currentDisplayChannel: string;
  bypassPinSuccessUI = false; // it is by design in some case
  private backButtonSubscribtion: Subscription;

  constructor(
    private userSessionService: UserSessionService,
    private authenticationService: AuthenticationService,
    private uniqueIDService: UniqueIDService,
    private navigator: Navigator,
    private logoutService: LogoutService,
    private configService: ConfigService,
    private eventService: EventsService,
    public platform: Platform,
    public successMessageService: SuccessMessageService
  ) {
    const mfaType = this.configService.getConfig().FEATURE_MFA_TYPE;
    const channels = this.configService.getConfig().FEATURE_MFA_CHANNELS as string;
    if(channels) {
      this.channels = channels.replace(/\s/g, '').split(','); //clean spaces in case configuration is not clean
    }
    if(!this.channels || this.channels.length == 0) {
      this.channels = ['SMS', 'EMAIL']; // default
    }

    if(mfaType && !this.channels.includes(mfaType)) {
      this.channels.push(mfaType);
    }
    this.currentDisplayChannel = mfaType ? mfaType : this.channels?.[0];
    this.userSessionService
      .refreshCustomer()
      .subscribe((customer: CustomerModel) => {
        if (customer) {
          this.customer = customer;
        }
      });
  }

  ngOnInit() {
    this.uniqueIDService.removeMFADeviceId();
    this.successMessageService.clearMessage();
    this.backButtonSubscribtion = this.platform.backButton.subscribeWithPriority(101, () => {
      this.closeModal();
    })
    this.authenticationService.sendLoginCode(this.customerId, this.currentDisplayChannel)
      .subscribe({
        next: response => {
          if (response.result == 1) {
            this.showMessageSMSSent = true;
          }
        },
        error: error => {
          switch (error.getErrorCode().toUpperCase()) {
            case 'SMS_FAILURE_CODE_SENT_BY_EMAIL':
              this.channels = ['EAMIL']
              this.currentDisplayChannel = 'EMAIL'
              this.showMessageSMSSent = true;
              this.error = null;  // do not set error
              return;
            case 'CUSTOMER_NOT_FOUND':
              error.setErrorMessage('Please login.');
              break;
            default:
              error.setErrorMessage(
                'An error has occured logging you in. Please try again.'
              );
              break;
          }
          this.error = error;
          this.content.scrollToTop(10);
        }
      });
  }
  
  ngOnDestroy() {
    if (this.backButtonSubscribtion) {
        this.backButtonSubscribtion.unsubscribe();
    }
  }

  closeModal = () => {
    if (!this.verifiedCode) {
        const onClose = this.onClose;
        if (onClose) {
          this.navigator.dismiss();
          onClose();
        } else {
          this.navigator.dismiss();
          return this.logoutService.onLogout();
        }
      } else {
        return setTimeout(() => {
          this.validateUserAction();
        }, 1000);
      }
  };

  goToUpdatePhoneNumber() {
    const loginAction = this.callback;
    this.navigator.navigateTo(
      'UpdatePhoneNumberComponent',
      {forceModal: true},
      {navigationFromSMSCodePage: true, callback: loginAction}
    );
  }

  activateCustomer = () => {
    this.eventService.publish('event:signupResult:flag');
    this.navigator.navigateTo("SignupResultComponent", { forceModal: true });
  }

  startKba(kycStep, kycFailErrors, kyc_attempts, idScanResult?) {
    this.userSessionService.setMFAApproval("true");
    if (kycStep && kycStep.toUpperCase() == 'PENDING_KBA') return this.navigator.navigateTo("IdologyKbaComponent");
    if (kycStep && kycStep.toUpperCase() == 'PENDING_RETRY') return this.navigator.navigateTo("InvalidKycComponent", {}, { allAttemptsUsed: false, dontCallAppInitNext: true, kyc_attempts });
    if (kycStep && kycStep.toUpperCase() == 'PENDING_SOVOS') return this.navigator.navigateTo("InvalidKycComponent", {}, { allAttemptsUsed: true, dontCallAppInitNext: true });
    if (kycStep && kycStep.toUpperCase() == 'PENDING_CS') return this.navigator.navigateTo("InvalidKycComponent", {}, { allAttemptsUsed: true, dontCallAppInitNext: true });
    if (kycStep && kycStep.toUpperCase() == 'PENDING_IDSCAN' && idScanResult === 'pending') return this.navigator.navigateTo("CamsComponent");
    if (kycStep && kycStep.toUpperCase() == 'PENDING_IDSCAN' && idScanResult !== 'pending') return this.navigator.navigateTo("CamsComponent");
    return this.navigator.navigateTo("InvalidKycComponent", {}, { kycFailErrors: kycFailErrors, dontCallAppInitNext: true });
  }

  public signUpStepTwo = () => {
    return this.navigator.navigateTo("SignupStepTwoComponent", {forceModal: true}, { KYCError: this.error});
  }

  public doKYC(): any {
    const customer_id = this.authenticationService.customer.customerId;
    const government_id = this.governmentId;
    const obj = {
      customer_id,
      government_id,
    }
    this.authenticationService.callVerifyKYC(obj).subscribe({
      next: (response) => {
        if (response.result == 1) {
          const payloadObject = response.payload;
          if ((Object.keys(payloadObject).length > 0 && payloadObject.constructor === Object) && payloadObject.hasOwnProperty('is_verified')) {
            const isVerified = response.payload.is_verified;
            const kycStep = response.payload.kyc_step;
            const kyc_attempts = response.payload.kyc_attempts;
            const kycFailErrors = response.payload.kyc_fail_errors;
            const idScanResult = response.payload.id_scan_result;
            if (isVerified === "true") return this.activateCustomer();
            this.startKba(kycStep, kycFailErrors, kyc_attempts, idScanResult);
          }
        }
      },
      error: (error) => {
        console.log("ERROR", error)
        this.error = error;
        this.signUpStepTwo();
      }
    });
  }

  validateUserAction() {
    const isMFAFirst = this.isMFAFirst;
    if (isMFAFirst) {
      this.doKYC();
    } else {
      const callBackFn = this.callback;
      if (callBackFn) {
        this.navigator.dismiss();
        callBackFn();
      } else {
        this.navigator.toLobby();
      }
    }
  }

  onValidatePinError(event) {
    this.error = event;
    if (this.error) {
      this.content.scrollToTop(10);
    }
  }

  onUpdatePhoneNumber() {
    this.goToUpdatePhoneNumber();
  }

  onValidatePinSuccess() {
    this.verifiedCode = true;
    this.error = null;
    const uniqueIDService = this.uniqueIDService;
    const mfcDeviceIdObservable = uniqueIDService.getMFADeviceIdObservable();
    if (mfcDeviceIdObservable) {
      mfcDeviceIdObservable.subscribe(deviceId => {
          uniqueIDService.setMFADeviceId(deviceId);
        })
        .unsubscribe();
    }
    this.userSessionService.setMFAApproval("true");
    this.userSessionService.setMFAStatus(eMfaStatuses.passed);
    if(this.bypassPinSuccessUI) {
      this.validateUserAction();
    }
  }

  onPinVerifiedUserAction() {
    this.validateUserAction();
  }

  onBack = () =>  {
    this.navigator.navigateTo("AuthenticatorOptionComponent", { forceModal: true },{ customerId: this.customerId, isMFAFirst: this.isMFAFirst, governmentId: this.governmentId, onClose: this.onClose });
  }
}
